/**
 * How every run-analysis dialog is opened.
 *
 * The long ones (Surface Difference, Deviation, Clearance, Layer Stack, …)
 * fill most of the viewport, which is exactly what makes an outside click so
 * easy to hit by accident — and losing one throws the whole configuration
 * away with no undo and no warning. So they opt out of dismiss-on-outside-
 * click. Escape and the ✕ still close them. Every dialog on
 * `AnalysisDialogShell` uses these options.
 */
import type { DialogOptions } from '@/providers/DialogProvider';

export const ANALYSIS_DIALOG_OPTIONS: DialogOptions = { dismissOnOutsideClick: false };

/**
 * DialogContent class for every dialog that renders `AnalysisDialogShell`.
 *
 * The SHELL owns the height cap (spec D9): this class makes DialogContent a
 * capped flex column, and the shell's body is its only `min-h-0 flex-1
 * overflow-y-auto` child, so header and footer stay pinned and the body
 * scrolls. A dialog migrated onto the shell must therefore STOP capping
 * itself — no `max-h-*` / `flex-col` on its `<form>` — or it reintroduces the
 * trap this replaced: a self-capped form inside a capped flex column is a flex
 * ITEM whose `min-height: auto` refuses to shrink below its content, so
 * `overflow-hidden` on the parent clips its bottom — the Run button — with no
 * scrollbar to hint anything is missing. (That is what happened to Deviation
 * and Surface Difference when one cap class was applied to all four run
 * dialogs at once, before the shell.)
 *
 * And every opener of a migrated dialog must pass this class: without it,
 * DialogContent is the default `grid`, nothing caps it, and a long form runs
 * off-screen. `lib/analyses/__tests__/shellOpeners.test.ts` enforces that.
 *
 * `overflow-clip`, not `overflow-hidden`: a hidden box can still be scrolled
 * by script, so focusing anything that sits outside the body's scroll area
 * (an `sr-only` input whose containing block is the dialog) scrolled the
 * whole dialog — header gone, empty space under the footer. Clip cannot.
 */
export const ANALYSIS_SHELL_CLASS = 'flex max-h-[80dvh] min-w-0 flex-col overflow-clip';

/** Compare surfaces only: the shell class, wider (two result cards, two role
 *  pickers and the add-on groups side by side). The other analysis dialogs
 *  keep the default width. */
export const COMPARE_SHELL_CLASS = `${ANALYSIS_SHELL_CLASS} sm:max-w-2xl`;
